import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { Html, Float } from '@react-three/drei';
import { Counter } from '@/components/Counter';
import { CHART_BARS } from '@/data/content';

const dummy = new THREE.Object3D();

const Bars = ({ progressRef, isMobile }) => {
  const inst = useRef();
  const animHeights = useRef(CHART_BARS.map(() => 0.1));
  const xs = useMemo(() => CHART_BARS.map((_, i) => (i - (CHART_BARS.length - 1) / 2) * 1.45), []);

  useEffect(() => {
    if (!inst.current) return;
    const c = new THREE.Color();
    const shades = ['#FFD700', '#C9971B', '#E0A82E', '#1E3A7B', '#38BDF8'];
    CHART_BARS.forEach((_, i) => inst.current.setColorAt(i, c.setStyle(shades[i])));
    if (inst.current.instanceColor) inst.current.instanceColor.needsUpdate = true;
  }, []);

  useFrame((state, delta) => {
    if (!inst.current) return;
    const p = progressRef ? progressRef.current : 0;
    
    CHART_BARS.forEach((b, i) => {
      // Base height grows smoothly on view + expands with scroll progress
      const scrollFactor = Math.max(0.7, Math.min(1.2, 0.7 + p * 0.5));
      const targetH = Math.max(0.5, b.h * scrollFactor);
      
      animHeights.current[i] = THREE.MathUtils.damp(animHeights.current[i], targetH, 4, delta);
      const h = animHeights.current[i];

      dummy.position.set(xs[i], h / 2, 0);
      dummy.scale.set(0.85, h, 0.85);
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      inst.current.setMatrixAt(i, dummy.matrix);
    });
    inst.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <group position={[0, -0.4, 0]}>
      <instancedMesh ref={inst} args={[undefined, undefined, CHART_BARS.length]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshPhysicalMaterial
          color="#ffffff"
          metalness={0.75}
          roughness={0.12}
          clearcoat={1}
          emissive="#C9971B"
          emissiveIntensity={0.15}
        />
      </instancedMesh>

      {!isMobile && CHART_BARS.map((b, i) => (
        <Html key={b.label} center position={[xs[i], 3.8, 0]} distanceFactor={8} className="bar-label">
          <div className="text-center select-none pointer-events-none bg-white/90 glass px-3 py-2 rounded-xl border border-ink/15 shadow-md">
            <p className="font-clash font-semibold text-brand-gold text-base md:text-xl leading-none">
              <Counter to={b.value} suffix={b.suffix} start duration={2.0} />
            </p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-ink/80 font-medium mt-1 whitespace-nowrap">{b.label}</p>
          </div>
        </Html>
      ))}
    </group>
  );
};

export const BarChartScene = ({ progressRef, dpr }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const effectiveDpr = isMobile ? Math.min(typeof dpr === 'number' ? dpr : 1, 1.2) : dpr;

  return (
    <div className="w-full h-full bg-[#FAFBFD]">
      <Canvas
        dpr={effectiveDpr}
        camera={{ position: isMobile ? [0, 4.2, 10.5] : [0, 3.8, 8.8], fov: isMobile ? 48 : 38 }}
        gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
        onCreated={({ camera }) => camera.lookAt(0, 1.2, 0)}
      >
        <color attach="background" args={['#FAFBFD']} />
        <fog attach="fog" args={['#FAFBFD', 10, 24]} />
        
        {/* Lights */}
        <ambientLight intensity={1.8} />
        <directionalLight position={[8, 12, 6]} intensity={3.5} color="#FFF8E7" />
        <pointLight position={[-6, 6, -4]} color="#1E3A7B" intensity={3.0} />
        <pointLight position={[6, 4, 6]} color="#FFD700" intensity={3.5} />

        <Float speed={0.8} rotationIntensity={0.01} floatIntensity={0.02}>
          <Bars progressRef={progressRef} isMobile={isMobile} />
        </Float>

        <gridHelper args={[24, 48, '#1E3A7B', '#C9971B']} position={[0, -0.42, 0]} material-opacity={0.2} material-transparent />
      </Canvas>
    </div>
  );
};
